import React, { useState } from 'react';
import {
  Card,
  TableCell,
  TableRow,
  Typography,
  TableContainer,
  IconButton,
  Divider
} from '@mui/material';
import {
  ActionCell,
  DynamicTable,
  TableSearch
} from 'src/components/DynamicTable/DynamicTable';
import { API } from '../../../config/config';
import { useUpdateModalStore } from 'src/zustandStore/store.update';
import { Perk } from '../../../interface/interface';
import LogoSpinner from 'src/components/LogoSpinner';
import { useAuth } from 'src/hooks/useAuth';
import { CreatePerkForm } from './CreatePerkForm';

interface PerksProps {
  perks: Perk[];
  refreshPerks: (updatedPerk?: any) => void;
  setPerksData: React.Dispatch<React.SetStateAction<any[]>>;
  loading: boolean;
  error: any;
}

// Function to refresh the perks data
const PerksContent: React.FC<PerksProps> = ({
  perks,
  setPerksData,
  loading,
  refreshPerks,
  error
}) => {
  const { settings, actions } = useUpdateModalStore();
  const { enhancedFetch } = useAuth();
  const [searchData, setSearchData] = useState<any[] | null>(null);
  const [sortBy, SetSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<'asc' | 'desc'>('asc');

  if (loading)
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LogoSpinner />
      </div>
    );

  return (
    <Card>
      {/* Use usePerkForm for editing perks */}
      {settings.modalOpen && settings.isEdit && CreatePerkForm({
        onSubmit: (formData) => {
          // Make API call to update the perk
          enhancedFetch(settings.editEndpoint, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData)
          })
            .then(response => {
              actions.setModal({ modalOpen: false, isEdit: false });
              refreshPerks(response); // Refresh perks after update
            })
            .catch(error => {
              console.error('Error updating perk:', error);
              actions.setModal({ modalOpen: false, isEdit: false });
            });
        },
        data: {
          title: settings.payload?.title || '',
          description: settings.payload?.description || ''
        },
        editData: settings.payload,
        openModal: settings.modalOpen && settings.isEdit,
        editModal: true,
        onClose: () => {
          actions.setModal({ modalOpen: false });
        },
        title: 'Update Perk',
        isLoading: false,
        fullWidth: false,
        maxWidth: 'lg'
      })}

      <Divider />
      <TableContainer>
        <DynamicTable
          sortByField={sortBy}
          onSort={(headerProperty) => {
            SetSortBy(headerProperty);
            setSortAsc(sortAsc === 'asc' ? 'desc' : 'asc');
          }}
          sort={sortAsc}
          searchElement={
            <TableSearch
              placeholder="Search for perks..."
              searchData={perks}
              setSearchData={setSearchData}
            />
          }
          headers={[
            { headerName: 'Title', field: 'title', width: 300 },
            { headerName: 'Description', field: 'description', flex: 1 },
            {
              headerName: 'Actions',
              field: 'actions',
              renderCell: (value) => {
                return (
                  <ActionCell
                    editEndpoint={`${API.HOST}/api/v1/perks/${value.row.id}`}
                    deleteEndpoint={`${API.HOST}/api/v1/perks/${value.row.id}`}
                    payload={value.row}
                    callback={() => {
                      refreshPerks(value.row);
                    }}
                  />
                );
              }
            }
          ]}

          rows={perks?.map((perk, index) => ({
            id: perk.id,
            title: perk.title,
            description: perk.description,
          }))}
        />


      </TableContainer>
    </Card>
  );
};

export default PerksContent;
